import { API_URL } from '../../config';
import { getSkillById } from '../gphSkillRegistry';
import {
  validateFreefireCalculatorSlots,
  buildMissingFreefireSlotsMessage,
  buildInvalidFreefireSlotsMessage
} from '../validation/freefireCalculatorSlotValidator';
import {
  renderFreefireCalculatorResult,
  renderFreefireCalculatorHumanError
} from '../renderers/freefireCalculatorResultRenderer';

const readResponseJson = async (response) => {
  try {
    return await response.json();
  } catch (error) {
    return {};
  }
};

const extractBackendMessage = (data = {}) => {
  const detail = data.detail || data.message || data.error;
  if (typeof detail === 'string') return detail;
  if (detail && typeof detail.message === 'string') return detail.message;
  return null;
};

export const buildFreefireCalculatorPayload = (slots = {}, { requestId = null, language = 'pt-BR' } = {}) => ({
  input: {
    platform: slots.platform,
    deviceTier: slots.deviceTier || 'medium',
    deviceModel: slots.deviceModel || null,
    ram: slots.ram,
    fps: slots.fps,
    hz: slots.hz,
    fingers: slots.fingers,
    playStyle: slots.playStyle,
    aimProblem: slots.aimProblem,
    dpi: slots.dpi || null,
    buttonSize: slots.buttonSize || null,
    currentSens: slots.currentSens || null
  },
  idempotencyKey: requestId,
  locale: language,
  language
});

export const prepareFreefireCalculatorSkill = ({
  slots = {},
  requestId = null,
  language = 'pt-BR'
} = {}) => {
  const skill = getSkillById('freefire_calculator');
  const validation = validateFreefireCalculatorSlots(slots);

  if (validation.invalidSlots.length > 0) {
    return {
      status: 'invalid',
      skill,
      slots: validation.normalizedSlots,
      missingSlots: validation.missingSlots,
      invalidSlots: validation.invalidSlots,
      message: buildInvalidFreefireSlotsMessage(validation.invalidSlots)
    };
  }

  if (validation.missingSlots.length > 0) {
    return {
      status: 'needs_slots',
      skill,
      slots: validation.normalizedSlots,
      missingSlots: validation.missingSlots,
      invalidSlots: [],
      message: buildMissingFreefireSlotsMessage(validation.missingSlots)
    };
  }

  return {
    status: 'ready',
    skill,
    slots: validation.normalizedSlots,
    missingSlots: [],
    invalidSlots: [],
    payload: buildFreefireCalculatorPayload(validation.normalizedSlots, { requestId, language }),
    message: null
  };
};

export const executeFreefireCalculatorSkill = async ({
  slots = {},
  fetchImpl = globalThis.fetch,
  apiBaseUrl = API_URL,
  authToken = null,
  getAuthToken = null,
  requestId = null,
  language = 'pt-BR'
} = {}) => {
  const prepared = prepareFreefireCalculatorSkill({ slots, requestId, language });

  if (prepared.status !== 'ready') {
    return prepared;
  }

  if (typeof fetchImpl !== 'function') {
    return {
      ...prepared,
      status: 'error',
      message: 'Nao consegui conectar a Calculadora Free Fire agora.'
    };
  }

  const payload = buildFreefireCalculatorPayload(prepared.slots, { requestId, language });

  try {
    const token = authToken || (typeof getAuthToken === 'function' ? await getAuthToken() : null);
    const response = await fetchImpl(`${apiBaseUrl}/freefire-calculator/calculate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      },
      body: JSON.stringify(payload)
    });
    const data = await readResponseJson(response);

    if (!response.ok) {
      return {
        ...prepared,
        status: 'blocked_or_error',
        payload,
        backendStatus: response.status,
        message: renderFreefireCalculatorHumanError({
          status: response.status,
          message: extractBackendMessage(data)
        })
      };
    }

    return {
      ...prepared,
      status: 'success',
      payload,
      result: data,
      renderedText: renderFreefireCalculatorResult(data)
    };
  } catch (error) {
    return {
      ...prepared,
      status: 'error',
      payload,
      message: 'Nao consegui conectar a Calculadora Free Fire agora.'
    };
  }
};

export default executeFreefireCalculatorSkill;
